import { ItemGroup } from "@/components/ui/item";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useEntityActivity,
  type EntityActivityFilters,
} from "@/hooks/use-entity-activity";
import { ActivityRow } from "./activity-row";

type EntityActivityPreviewProps = EntityActivityFilters & {
  // How many rows to show in the rail before the "See all" affordance.
  rowCount?: number;
  // Click handler for the "See all" affordance. Typically scrolls/swaps to
  // the full Activity tab on the parent detail page.
  onSeeAll?: () => void;
};

export function EntityActivityPreview({
  rowCount = 4,
  onSeeAll,
  ...rest
}: EntityActivityPreviewProps) {
  const filters = { ...rest, limit: rowCount } as EntityActivityFilters;
  const { data, isLoading, isError } = useEntityActivity(filters);

  if (isLoading) {
    return (
      <div className="space-y-2" aria-busy="true">
        {Array.from({ length: rowCount }).map((_, i) => (
          <Skeleton key={i} className="h-9 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="typo-label text-[color:var(--color-text-lo)] py-2">
        Couldn&rsquo;t load activity
      </p>
    );
  }

  const rows = (data?.activity ?? []).slice(0, rowCount);
  if (rows.length === 0) {
    return (
      <p className="typo-label text-[color:var(--color-text-lo)] py-2">
        No activity yet
      </p>
    );
  }

  const total = data?.total ?? rows.length;
  const showSeeAll = onSeeAll && total > rowCount;

  return (
    <div className="space-y-2">
      <ItemGroup className="gap-1">
        {rows.map((r) => (
          <ActivityRow key={r.id} row={r} compact />
        ))}
      </ItemGroup>
      {showSeeAll && (
        <button
          type="button"
          onClick={onSeeAll}
          className="typo-label text-[color:var(--color-accent)] hover:text-[color:var(--color-text-hi)]"
        >
          See all ({total})
        </button>
      )}
    </div>
  );
}
